export type SessionSyncMessage =
  | { type: 'activity'; userId: string; occurredAt: number }
  | { type: 'logout'; userId: string; occurredAt: number }
  | { type: 'expired'; userId: string; occurredAt: number };

export interface SessionSyncChannel {
  publish(message: SessionSyncMessage): void;
  close(): void;
}

const CHANNEL_NAME = 'ochal.auth.session.v1';
const STORAGE_KEY = 'ochal.auth.session.sync.v1';

/**
 * Synchronizes non-sensitive session lifecycle signals. Tokens and session
 * payloads are deliberately never sent between tabs.
 */
export function createSessionSyncChannel(
  onMessage: (message: SessionSyncMessage) => void,
): SessionSyncChannel {
  if (typeof window === 'undefined') return { publish: () => undefined, close: () => undefined };

  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.addEventListener('message', (event: MessageEvent<unknown>) => {
      if (isSessionSyncMessage(event.data)) onMessage(event.data);
    });
    return {
      publish: (message) => channel.postMessage(message),
      close: () => channel.close(),
    };
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      const parsed: unknown = JSON.parse(event.newValue);
      if (isSessionSyncMessage(parsed)) onMessage(parsed);
    } catch {
      // Ignore malformed or unrelated storage events.
    }
  };
  window.addEventListener('storage', onStorage);
  return {
    publish(message) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Storage can be unavailable in privacy modes; the current tab remains safe.
      }
    },
    close() {
      window.removeEventListener('storage', onStorage);
    },
  };
}

function isSessionSyncMessage(value: unknown): value is SessionSyncMessage {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<SessionSyncMessage>;
  return (
    (candidate.type === 'activity' || candidate.type === 'logout' || candidate.type === 'expired') &&
    typeof candidate.userId === 'string' &&
    Number.isFinite(candidate.occurredAt)
  );
}
